import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  login,
  logout as logoutApi,
  register,
  refreshToken as refreshTokenApi,
  getCurrentUser,
} from '../api/auth';
import { AuthResponse, LoginParams, RegisterParams } from '../api/auth';
import {
  clearAuthSession,
  clearTenantSession,
  getStoredAuthSession,
  saveAuthSession,
} from '@saasplatform/auth';

interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  isSuperAdmin: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  initialized: boolean;
  loading: boolean;
  error: string | null;
}

const storedSession = getStoredAuthSession();

const initialState: AuthState = {
  isAuthenticated: false,
  user: storedSession?.user ?? null,
  accessToken: null,
  refreshToken: null,
  initialized: false,
  loading: false,
  error: null,
};

// 异步thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (params: LoginParams) => {
    const response = await login(params);
    return {
      ...response,
      persistLogin: !!params.keepLogin30Days,
    };
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (params: RegisterParams) => {
    const response = await register(params);
    return response;
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async () => {
    const response = await getCurrentUser();
    return response;
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshToken',
  async () => {
    const response = await refreshTokenApi({});
    return response;
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCurrentUser();
      return response;
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  await logoutApi();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticated: (
      state,
      action: PayloadAction<{ accessToken: string; user?: User | null }>,
    ) => {
      state.isAuthenticated = true;
      state.initialized = true;
      state.accessToken = null;
      state.refreshToken = null;
      if (action.payload.user !== undefined) {
        state.user = action.payload.user;
      }
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.initialized = true;
      state.loading = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      clearAuthSession();
      clearTenantSession();
    },
    markUnauthenticated: (state) => {
      state.isAuthenticated = false;
      state.initialized = true;
      state.loading = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      clearAuthSession();
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.initialized = true;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      clearAuthSession();
      clearTenantSession();
    });
    builder.addCase(logoutUser.rejected, (state) => {
      state.isAuthenticated = false;
      state.initialized = true;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      clearAuthSession();
      clearTenantSession();
    });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = null;
        state.refreshToken = null;
        saveAuthSession(action.payload, {
          persistLogin: action.payload.persistLogin,
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.error.message || '登录失败';
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<AuthResponse>) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = null;
        state.refreshToken = null;
        saveAuthSession(action.payload, { persistLogin: false });
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.error.message || '注册失败';
      });

    // Fetch current user
    builder
      .addCase(fetchCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.initialized = true;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        clearAuthSession();
      });

    builder
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        clearAuthSession();
      });

    // Refresh token
    builder
      .addCase(refreshAccessToken.fulfilled, (state, action: PayloadAction<AuthResponse>) => {
        state.initialized = true;
        state.accessToken = null;
        state.refreshToken = null;
        saveAuthSession(action.payload);
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        clearAuthSession();
      });
  },
});

export const { setAuthenticated, logout, markUnauthenticated, clearError } = authSlice.actions;
export default authSlice.reducer;
